import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Button, Statistic, Space, Spin } from 'antd';
import { PlusOutlined, UserOutlined, BarChartOutlined } from '@ant-design/icons';
import projectService from '../services/projectService';
import { Project } from '../types/project';

const { Title, Text, Paragraph } = Typography;

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      fetchProjects();
    }
  }, [isAuthenticated, authLoading]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const data = await projectService.getUserProjects();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      if (error instanceof Error && error.message.includes('401')) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const metrics = [
    { id: 'totalTestCases', value: 150, label: t('dashboard.metrics.totalTestCases') },
    { id: 'passedTests', value: 120, label: t('dashboard.metrics.passedTests') },
    { id: 'failedTests', value: 25, label: t('dashboard.metrics.failedTests') },
    { id: 'blockedTests', value: 5, label: t('dashboard.metrics.blockedTests') },
    { id: 'bugs', value: 12, label: t('dashboard.metrics.totalBugs') },
    { id: 'criticalBugs', value: 3, label: t('dashboard.metrics.criticalBugs') },
  ];

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card>
            <Title level={2}>{t('dashboard.welcome', { name: user.name })}</Title>
            <Paragraph type="secondary">{t('dashboard.description')}</Paragraph>
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            <Title level={3}>{t('dashboard.yourProjects')}</Title>
            {projects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px' }}>
                <Paragraph>{t('dashboard.noProjects')}</Paragraph>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/projects')}
                >
                  {t('dashboard.createFirstProject')}
                </Button>
              </div>
            ) : (
              <Row gutter={[24, 24]}>
                {projects.map((project) => (
                  <Col xs={24} sm={12} md={8} key={project.id}>
                    <Card
                      cover={project.image_url ? (
                        <img
                          alt={project.name}
                          src={project.image_url}
                          style={{ height: 140, objectFit: 'cover' }}
                        />
                      ) : null}
                      actions={[
                        <Button
                          type="link"
                          onClick={() => navigate(`/projects/${project.id}`)}
                        >
                          {t('projects.actions.view')}
                        </Button>,
                        <Button
                          type="link"
                          onClick={() => navigate(`/projects/${project.id}/manage`)}
                        >
                          {t('projects.actions.manage')}
                        </Button>,
                      ]}
                    >
                      <Card.Meta
                        title={project.name}
                        description={
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Paragraph>{project.description || t('projects.noDescription')}</Paragraph>
                            <Text type="secondary">
                              {t('projects.domain.label')}: {project.domain || t('projects.domain.notSpecified')}
                            </Text>
                            <Text type="secondary">
                              {t('dashboard.projectInfo.createdAt')}: {project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}
                            </Text>
                            <Text type="secondary">
                              {t('dashboard.projectInfo.updatedAt')}: {project.updated_at ? new Date(project.updated_at).toLocaleDateString() : '-'}
                            </Text>
                          </Space>
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            <Title level={3}>{t('dashboard.metrics.title')}</Title>
            <Row gutter={[24, 24]}>
              {metrics.map((metric) => (
                <Col xs={24} sm={12} md={8} key={metric.id}>
                  <Card>
                    <Statistic
                      title={metric.label}
                      value={metric.value}
                      prefix={<BarChartOutlined />}
                    />
                  </Card>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card>
            <Paragraph>
              {t('dashboard.welcome', { name: user.name })}
            </Paragraph>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={t('dashboard.stats')}>
            <Space direction="vertical">
              <Text>
                {t('dashboard.totalUsers')}: 0
              </Text>
              <Text>
                {t('dashboard.activeUsers')}: 0
              </Text>
            </Space>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title={t('dashboard.actions')}>
            <Space>
              <Button type="primary" icon={<UserOutlined />}>
                {t('dashboard.addUser')}
              </Button>
              <Button type="default">
                {t('dashboard.viewReports')}
              </Button>
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage; 